const FRESHCHAT_TOKEN = "df0087f5-c9e9-4adc-b51e-02dac85c7a64";
const FRESHCHAT_HOST = "https://wchat.freshchat.com";
const FRESHCHAT_SCRIPT_ID = "freshchat-widget";

type FreshchatSettings = {
  token?: string;
  host?: string;
  externalId?: string;
  restoreId?: string;
  onInit?: () => void;
  [key: string]: unknown;
};

type FreshchatUserCreatePayload = {
  firstName?: string;
  lastName?: string;
  email?: string;
  externalId?: string;
  restoreId?: string;
  meta?: Record<string, unknown>;
};

type FreshchatCreateResponse = {
  success?: boolean;
  status?: number;
  data?: {
    restoreId?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export interface FreshchatUserDetails {
  firstName: string;
  lastName: string;
  email: string;
  schoolGroup?: string;
  school?: string;
  externalId?: string;
  restoreId?: string;
}

interface FreshchatEventResponse {
  data?: {
    restoreId?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface FreshchatWidget {
  init: (config: { token: string; host: string }) => void;
  destroy?: () => void;
  on: (eventName: string, handler: (response: FreshchatEventResponse) => void) => void;
  off?: (eventName: string, handler: (response: FreshchatEventResponse) => void) => void;
  isInitialized?: () => boolean;
  setExternalId?: (externalId: string) => void;
  setRestoreId?: (restoreId: string) => void;
  user: {
    clear: () => void;
    setProperties: (properties: Record<string, unknown>) => void;
    setFirstName?: (firstName: string) => void;
    setLastName?: (lastName: string) => void;
    setEmail?: (email: string) => void;
    setExternalId?: (externalId: string) => void;
    setRestoreId?: (restoreId: string) => void;
    create?: (
      payload: FreshchatUserCreatePayload,
      callback?: (response: unknown) => void
    ) => void;
    get?: (callback: (response: unknown) => void) => void;
  };
}

declare global {
  interface Window {
    fcWidget?: FreshchatWidget;
    fcSettings?: FreshchatSettings;
  }
}

let initializationPromise: Promise<void> | null = null;
let widgetReady = false;
let widgetConfigured = false;
const readyResolvers: Array<() => void> = [];
let restoreIdCallback: ((restoreId: string) => void) | null = null;
let lastRestoreId: string | null = null;
let lastUserSignature: string | null = null;

const isPromiseLike = (value: unknown): value is PromiseLike<unknown> => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as { then?: unknown };
  return typeof candidate.then === "function";
};

const isCreateErrorResponse = (response: unknown): response is { success?: boolean; status?: number } => {
  if (typeof response !== "object" || response === null) {
    return false;
  }

  const candidate = response as { success?: unknown; status?: unknown };

  return typeof candidate.success === "boolean" || typeof candidate.status === "number";
};

const extractRestoreId = (candidate: FreshchatEventResponse | FreshchatCreateResponse | undefined): string | undefined => {
  if (!candidate) {
    return undefined;
  }

  if (typeof candidate.data === "object" && candidate.data && "restoreId" in candidate.data) {
    const fromData = (candidate.data as { restoreId?: unknown }).restoreId;
    if (typeof fromData === "string") {
      return fromData;
    }
  }

  if ("restoreId" in candidate && typeof (candidate as { restoreId?: unknown }).restoreId === "string") {
    return (candidate as { restoreId?: string }).restoreId;
  }

  return undefined;
};

const restoreExistingFreshchatUser = (details: FreshchatUserDetails, meta: Record<string, unknown>): boolean => {
  if (typeof window === "undefined" || !window.fcWidget) {
    return false;
  }

  const widget = window.fcWidget;
  const userApi = widget.user;
  if (!userApi) {
    return false;
  }
  const restoreId = typeof details.restoreId === "string" ? details.restoreId.trim() : "";

  if (!restoreId) {
    return false;
  }

  console.log("Restoring Freshchat user with restoreId:", restoreId);

  let restored = false;

  if (details.externalId && typeof userApi.setExternalId === "function") {
    try {
      userApi.setExternalId(details.externalId);
    } catch (error) {
      console.error("Freshchat setExternalId failed", error);
    }
  }

  if (typeof userApi.setProperties === "function" && Object.keys(meta).length > 0) {
    try {
      userApi.setProperties(meta);
    } catch (error) {
      console.error("Freshchat setProperties failed", error);
    }
  }

  if (details.email && typeof userApi.setEmail === "function") {
    try {
      userApi.setEmail(details.email);
    } catch (error) {
      console.error("Freshchat setEmail failed", error);
    }
  }

  if (details.firstName && typeof userApi.setFirstName === "function") {
    try {
      userApi.setFirstName(details.firstName);
    } catch (error) {
      console.error("Freshchat setFirstName failed", error);
    }
  }

  if (details.lastName && typeof userApi.setLastName === "function") {
    try {
      userApi.setLastName(details.lastName);
    } catch (error) {
      console.error("Freshchat setLastName failed", error);
    }
  }

  if (typeof userApi.setRestoreId === "function") {
    try {
      const restoreIdChanged = restoreId !== lastRestoreId;

      userApi.setRestoreId(restoreId);
      lastRestoreId = restoreId;
      restored = true;

      if (restoreIdChanged && restoreIdCallback) {
        try {
          restoreIdCallback(restoreId);
        } catch (error) {
          console.error("Freshchat restore ID handler failed", error);
        }
      }
    } catch (error) {
      console.error("Freshchat setRestoreId failed", error);
    }
  } else {
    console.warn("Freshchat setRestoreId API is unavailable; user may not be restored");
  }

  return restored;
};

const createFreshchatUser = (details: FreshchatUserDetails | null) => {
  console.log("Creating/updating Freshchat user with details:", details);
  if (typeof window === "undefined" || !window.fcWidget?.user?.create || !details) {
    return;
  }

  const trimmedRestoreId = typeof details.restoreId === "string" ? details.restoreId.trim() : "";
  const normalizedDetails: FreshchatUserDetails = trimmedRestoreId
    ? { ...details, restoreId: trimmedRestoreId }
    : details;

  const signaturePayload = {
    externalId: normalizedDetails.externalId ?? "",
    email: normalizedDetails.email ?? "",
    firstName: normalizedDetails.firstName ?? "",
    lastName: normalizedDetails.lastName ?? "",
    schoolGroup: normalizedDetails.schoolGroup ?? "",
    school: normalizedDetails.school ?? "",
    restoreId: trimmedRestoreId,
  };

  const signature = JSON.stringify(signaturePayload);

  if (lastUserSignature === signature) {
    console.log("Skipping user create/update, details have not changed.");
    return;
  }

  console.log("Freshchat user create/update signature:", signature);
  lastUserSignature = signature;

  const meta: Record<string, unknown> = {};
  if (normalizedDetails.schoolGroup) {
    meta.cf_school_group = normalizedDetails.schoolGroup;
  }
  if (normalizedDetails.school) {
    meta.cf_school = normalizedDetails.school;
  }

  if (trimmedRestoreId) {
    const restored = restoreExistingFreshchatUser(normalizedDetails, meta);

    if (restored) {
      return;
    }

    console.warn("Freshchat restore via setRestoreId failed, falling back to user.create");
  }

  const payload: FreshchatUserCreatePayload = {
    firstName: normalizedDetails.firstName,
    lastName: normalizedDetails.lastName,
    email: normalizedDetails.email,
  };

  if (normalizedDetails.externalId) {
    payload.externalId = normalizedDetails.externalId;
  }

  if (Object.keys(meta).length > 0) {
    payload.meta = meta;
  }

  let successHandled = false;

  const handleSuccess = (response: unknown) => {
    if (successHandled) {
      return;
    }

    console.log("Freshchat user.create callback invoked");
    if (isCreateErrorResponse(response) && response.success === false) {
      console.warn("Freshchat user.create returned an error status", response);
      handleFailure(response);
      return;
    }

    successHandled = true;
    const candidate = response as FreshchatEventResponse | FreshchatCreateResponse | undefined;
    console.log("Freshchat user.create response:", response);
    const restoreIdFromPayload = extractRestoreId(candidate);

    if (typeof restoreIdFromPayload === "string") {
      lastRestoreId = restoreIdFromPayload;

      if (restoreIdCallback) {
        try {
          restoreIdCallback(restoreIdFromPayload);
        } catch (error) {
          console.error("Freshchat restore ID handler failed", error);
        }
      }
    }
  };

  const handleFailure = (error: unknown) => {
    console.error("Freshchat user.create failed", error);

    if (!successHandled && lastUserSignature === signature) {
      lastUserSignature = null;
    }
  };

  try {
  console.log("Freshchat user.create payload:", payload);
    const result = window.fcWidget.user.create(payload, handleSuccess);

    if (isPromiseLike(result)) {
      result.then(handleSuccess, handleFailure);
    }
  } catch (error) {
    handleFailure(error);
  }
};

const markWidgetReady = () => {
  console.log("Freshchat widget is ready");
  if (widgetReady) {
    return;
  }

  widgetReady = true;

  while (readyResolvers.length) {
    const resolver = readyResolvers.shift();
    resolver?.();
  }
};

const handleRestoreEvent = (response: FreshchatEventResponse) => {
  console.log("Freshchat restore event", response);
  const restoreId = response?.data?.restoreId;

  if (!restoreId || restoreId === lastRestoreId) {
    return;
  }

  lastRestoreId = restoreId;

  if (restoreIdCallback) {
    try {
      restoreIdCallback(restoreId);
    } catch (error) {
      console.error("Freshchat restore ID handler failed", error);
    }
  }
};

const configureWidget = () => {
  if (typeof window === "undefined" || !window.fcWidget) {
    return;
  }

  if (widgetConfigured) {
    if (!widgetReady) {
      markWidgetReady();
    }
    return;
  }

  widgetConfigured = true;

  window.fcWidget.on("widget:loaded", markWidgetReady);
  window.fcWidget.on("user:created", handleRestoreEvent);
  window.fcWidget.on("user created", handleRestoreEvent);
  window.fcWidget.on("user:restored", handleRestoreEvent);
  window.fcWidget.on("user:updated", handleRestoreEvent);

  if (window.fcWidget.isInitialized?.()) {
    markWidgetReady();
    return;
  }

  try {
    window.fcWidget.init({
      token: FRESHCHAT_TOKEN,
      host: FRESHCHAT_HOST,
    });
  } catch (error) {
    console.error("Failed to initialise Freshchat", error);
  }

  // This timeout is unreliable and can cause race conditions.
  // The 'widget:loaded' event is the correct source of truth.
  // setTimeout(markWidgetReady, 3000);
};

export const initFreshchat = (): Promise<void> => {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (widgetReady) {
    return Promise.resolve();
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = new Promise((resolve, reject) => {
    // The promise will now resolve inside markWidgetReady,
    // which is called by the 'widget:loaded' event.
    readyResolvers.push(resolve);

    const existingScript = document.getElementById(FRESHCHAT_SCRIPT_ID);

    if (existingScript) {
      if (window.fcWidget) {
        configureWidget();
      } else {
        // If script exists but widget doesn't, it's a bad state. Remove and reload.
        existingScript.remove();
        // We'll fall through to the script creation logic below.
      }
    }

    // Re-check for script in case it was just removed.
    if (!document.getElementById(FRESHCHAT_SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = FRESHCHAT_SCRIPT_ID;
      script.async = true;
      script.src = `${FRESHCHAT_HOST}/js/widget.js`;

      script.onload = () => {
        if (!window.fcWidget) {
          reject(new Error("Freshchat widget failed to load"));
          return;
        }
        configureWidget();
      };

      script.onerror = () => {
        reject(new Error("Failed to load Freshchat widget"));
      };

      document.head.appendChild(script);
    }
  });

  return initializationPromise;
};

export const identifyFreshchatUser = async (details: FreshchatUserDetails | null) => {
  // Aggressively teardown to ensure a clean state.
  teardownFreshchat();

  if (!details) {
    // If no user details are provided (logout), just stop after the teardown.
    return;
  }

  try {
    // Wait for the teardown to complete and the event loop to clear.
    await new Promise(resolve => setTimeout(resolve, 100));

    // initFreshchat now waits for the widget to be fully ready.
    await initFreshchat();

    // The widget is ready, so we can call create user directly.
    createFreshchatUser(details);
  } catch (error) {
    console.error("Failed to identify Freshchat user:", error);
  }
};

export const onFreshchatRestoreId = (handler: (restoreId: string) => void) => {
  restoreIdCallback = handler;
};

export const resetFreshchat = () => {
  // Clear user immediately instead of enqueuing
  if (typeof window !== "undefined" && window.fcWidget) {
    try {
      window.fcWidget.user.clear();
    } catch (error) {
      console.error("Failed to reset Freshchat", error);
    }
  }

  lastRestoreId = null;
  lastUserSignature = null;
};

export const teardownFreshchat = () => {
  if (typeof window === "undefined") {
    return;
  }

  // Attempt to destroy the widget if it exists, but don't let it stop other cleanup.
  if (window.fcWidget) {
    try {
      window.fcWidget.destroy?.();
    } catch (error) {
      console.error("Failed to destroy Freshchat", error);
    }
  }

  // Always clear the global variables to prevent auto-initialization on script reload.
  window.fcWidget = undefined;
  window.fcSettings = undefined;

  // Always try to remove the script tag from the DOM.
  const existingScript = document.getElementById(FRESHCHAT_SCRIPT_ID);
  if (existingScript?.parentNode) {
    existingScript.parentNode.removeChild(existingScript);
  }

  // Always reset all internal state variables.
  readyResolvers.length = 0;
  initializationPromise = null;
  widgetReady = false;
  widgetConfigured = false;
  lastRestoreId = null;
  lastUserSignature = null;
};

export const getLastRestoreId = (): string | null => lastRestoreId;
